import { AIReadyHealthFilter, AIReadyHealthTrendPoint } from "../../../../slices/BaselineSecurityHealth.slice";

type NormalizedRiskBand = "High" | "Moderate" | "Low";

export interface AdoptionDistribution {
  low: number;
  moderate: number;
  high: number;
  totalTenants: number;
}

export interface AdoptionWoW {
  low: number | null;
  lowPercentage: number | null;
  moderate: number | null;
  moderatePercentage: number | null;
  high: number | null;
  highPercentage: number | null;
  currentDate: string | null;
  previousDate: string | null;
}

export const relativeChangePercentage = (
  current: number | null | undefined,
  previous: number | null | undefined
): number | null =>
  current === null || current === undefined || previous === null || previous === undefined || previous === 0
    ? null
    : ((current - previous) / previous) * 100;

const INITIATIVE_RISK_FILTER_KEYS = {
  Overall: "overallRiskStatus",
  Baseline: "baselineRiskStatus",
  Passkey: "passkeyRiskStatus",
  Patching: "patchingRiskStatus",
  Endpoint: "endpointRiskStatus",
  Protection: "protectionRiskStatus",
  Hygiene: "hygieneRiskStatus",
  DormantAccount: "dormantAccountRiskStatus",
  Vulnerabilities: "vulnerabilitiesRiskStatus",
  Monitoring: "monitoringRiskStatus",
} as const satisfies Record<string, keyof AIReadyHealthFilter>;

export const applyInitiativeRiskFilter = (
  filter: AIReadyHealthFilter,
  initiative: keyof typeof INITIATIVE_RISK_FILTER_KEYS,
  adoptionBand: string
): AIReadyHealthFilter => ({
  ...filter,
  [INITIATIVE_RISK_FILTER_KEYS[initiative]]: adoptionBand,
});

export const latestAdoptionTrendPoint = (
  points: readonly AIReadyHealthTrendPoint[],
  initiative: string
): AIReadyHealthTrendPoint | null =>
  points.reduce<AIReadyHealthTrendPoint | null>((latest, point) => {
    if (point.initiative !== initiative) return latest;
    if (!latest || (point.factDate ?? "") >= (latest.factDate ?? "")) return point;
    return latest;
  }, null);

export const adoptionWoWFromTrend = (
  points: readonly AIReadyHealthTrendPoint[],
  initiative: string
): AdoptionWoW => {
  const initiativePoints = points
    .filter((point) => point.initiative === initiative && point.factDate)
    .map((point) => ({ point, timestamp: Date.parse(point.factDate as string) }))
    .filter(({ timestamp }) => Number.isFinite(timestamp))
    .sort((a, b) => b.timestamp - a.timestamp);

  if (initiativePoints.length < 2) {
    return {
      low: null,
      lowPercentage: null,
      moderate: null,
      moderatePercentage: null,
      high: null,
      highPercentage: null,
      currentDate: null,
      previousDate: null,
    };
  }

  const current = initiativePoints[0].point;
  const previous = initiativePoints[1].point;
  return {
    low: current.low - previous.low,
    lowPercentage: relativeChangePercentage(current.low, previous.low),
    moderate: current.moderate - previous.moderate,
    moderatePercentage: relativeChangePercentage(current.moderate, previous.moderate),
    high: current.high - previous.high,
    highPercentage: relativeChangePercentage(current.high, previous.high),
    currentDate: current.factDate,
    previousDate: previous.factDate,
  };
};

export const parseEngagementCohorts = (raw: string | null | undefined): string[] =>
  (raw ?? "")
    .split(",")
    .map((cohort) => cohort.trim())
    .filter(Boolean);

export const matchesEngagementCohort = (
  raw: string | null | undefined,
  selected: ReadonlySet<string>
): boolean =>
  selected.size === 0 || parseEngagementCohorts(raw).some((cohort) => selected.has(cohort));

export const adoptionDistributionFromRiskBands = (
  bands: readonly NormalizedRiskBand[]
): AdoptionDistribution => {
  const distribution: AdoptionDistribution = {
    low: 0,
    moderate: 0,
    high: 0,
    totalTenants: bands.length,
  };

  for (const band of bands) {
    if (band === "High") distribution.low += 1;
    else if (band === "Moderate") distribution.moderate += 1;
    else distribution.high += 1;
  }

  return distribution;
};
